import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ujmsCaseEvidence, ujmsLogEvidence, ujmsTransferEvidence, ujmsDisposeEvidence, ujmsVerifyEvidence,
} from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, btnDanger, fmtDT, can, EmptyState } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Box, ShieldCheck, ShieldAlert, Link2, Paperclip } from "lucide-react";
import { MediaLibrary } from "../enterprise/media/MediaLibrary";

const CATEGORIES = ["WEAPON", "DOCUMENT", "DIGITAL", "BIOLOGICAL", "NARCOTICS", "PROPERTY", "MEDIA", "OTHER"];

export const EvidenceTab = ({ caseId, access }) => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [dlg, setDlg] = useState(null); // {type, item}
  const [verify, setVerify] = useState({});
  const [attachTo, setAttachTo] = useState("");
  const [mediaCount, setMediaCount] = useState(null);
  const load = useCallback(() => {
    ujmsCaseEvidence(caseId).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const runVerify = async (it) => {
    try {
      const r = await ujmsVerifyEvidence(it.id);
      setVerify((v) => ({ ...v, [it.id]: r.data }));
      r.data.valid ? toast.success(r.data.detail) : toast.error(r.data.detail);
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
  };

  if (!items) return <Panel testId="tab-evidence"><div className="text-sm text-slate-400 p-4">Loading evidence…</div></Panel>;
  const manage = can(access, "evidence.manage");

  return (
    <Panel title={`Evidence & media — tamper-evident chain of custody${mediaCount != null ? ` · ${mediaCount} media file${mediaCount === 1 ? "" : "s"}` : ""}`} testId="tab-evidence"
      action={manage && <button className={btnPrimary} onClick={() => setDlg({ type: "add" })} data-testid="ev-add-btn">Log Item</button>}>
      {items.length === 0 ? (
        <EmptyState icon={Box} title="No evidence logged" note="Items logged here carry a hash-chained custody log — any alteration is detectable." testId="ev-empty" />
      ) : (
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Item</th><th className={thCls}>Description</th><th className={thCls}>Category</th>
            <th className={thCls}>Custodian</th><th className={thCls}>Status</th><th className={thCls}>Chain</th><th className={thCls}></th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <React.Fragment key={it.id}>
                <tr className="hover:bg-slate-50 align-top" data-testid={`ev-row-${it.item_number}`}>
                  <td className={`${tdCls} font-mono text-xs`}>{it.item_number}</td>
                  <td className={tdCls}>{it.description}<div className="text-[11px] text-slate-400">Collected {it.collected_at} by {it.collected_by}</div></td>
                  <td className={tdCls}><Badge value={it.category} /></td>
                  <td className={`${tdCls} text-xs`}>{it.current_custodian}</td>
                  <td className={tdCls}><Badge value={it.status} testId={`ev-status-${it.item_number}`} /></td>
                  <td className={tdCls}>
                    <button className="text-xs text-blue-900 underline flex items-center gap-1" onClick={() => setOpen(open === it.id ? null : it.id)}
                      data-testid={`ev-chain-toggle-${it.item_number}`}>
                      <Link2 className="w-3 h-3" /> {(it.chain || []).length} entries
                    </button>
                    {verify[it.id] && (
                      <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase rounded-sm px-1.5 py-0.5 border ${verify[it.id].valid ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}
                        data-testid={`ev-verify-result-${it.item_number}`}>
                        {verify[it.id].valid ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                        {verify[it.id].valid ? "Intact" : "TAMPERED"}
                      </div>
                    )}
                  </td>
                  <td className={tdCls}>
                    <div className="flex flex-col gap-1.5">
                      <button className={btnGhost} onClick={() => runVerify(it)} data-testid={`ev-verify-${it.item_number}`}>Verify Chain</button>
                      {manage && <button className={btnGhost} onClick={() => { setAttachTo(it.id); document.querySelector("[data-testid='media-library']")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} data-testid={`ev-attach-media-${it.item_number}`}><Paperclip className="w-3 h-3" /> Attach media</button>}
                      {manage && !["DESTROYED", "RELEASED", "RETURNED"].includes(it.status) && <>
                        <button className={btnGhost} onClick={() => setDlg({ type: "transfer", item: it })} data-testid={`ev-transfer-${it.item_number}`}>Transfer</button>
                        <button className={btnDanger} onClick={() => setDlg({ type: "dispose", item: it })} data-testid={`ev-dispose-${it.item_number}`}>Dispose</button>
                      </>}
                    </div>
                  </td>
                </tr>
                {open === it.id && (
                  <tr>
                    <td colSpan={7} className="bg-slate-50 border-b border-slate-200 px-4 py-3" data-testid={`ev-chain-${it.item_number}`}>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-2">Chain of custody (SHA-256 hash-linked, append-only)</div>
                      <div className="space-y-1.5">
                        {(it.chain || []).map((e) => (
                          <div key={e.id} className="text-xs text-slate-600 flex flex-wrap items-baseline gap-2">
                            <span className="font-mono text-slate-400">#{e.sequence}</span>
                            <Badge value={e.action} />
                            <span>{e.from_custodian} → {e.to_custodian}</span>
                            <span className="text-slate-400">{e.purpose}</span>
                            <span className="font-mono text-slate-400">{fmtDT(e.created_at)} · {e.actor}</span>
                            <span className="font-mono text-[10px] text-slate-300" title={e.entry_hash}>⛓ {e.entry_hash.slice(0, 12)}…</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-3 border-t border-slate-200 pt-2">
        <MediaLibrary caseId={caseId} access={access} evidenceItems={items} defaultEvidenceId={attachTo} onCountChange={setMediaCount} />
      </div>
      <EvidenceDialogs dlg={dlg} caseId={caseId} onClose={() => setDlg(null)} onDone={load} />
    </Panel>
  );
};

function EvidenceDialogs({ dlg, caseId, onClose, onDone }) {
  const [f, setF] = useState({});
  useEffect(() => { setF({}); }, [dlg?.type, dlg?.item?.id]);
  if (!dlg) return null;
  const done = (msg) => { toast.success(msg); onClose(); onDone(); };
  const fail = (e) => toast.error(e.response?.data?.detail || "Failed");
  const { type, item } = dlg;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`ev-dialog-${type}`}>
        {type === "add" && <>
          <DialogHeader><DialogTitle>Log evidence item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Description" required><input className={inputCls} value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ev-add-description" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category" required>
                <select className={selectCls} value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="ev-add-category">
                  <option value="">Select…</option>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Collected by" required><input className={inputCls} value={f.collected_by || ""} onChange={(e) => setF({ ...f, collected_by: e.target.value })} data-testid="ev-add-collected-by" /></Field>
              <Field label="Collected on"><input type="date" className={inputCls} value={f.collected_at || ""} onChange={(e) => setF({ ...f, collected_at: e.target.value })} data-testid="ev-add-collected-at" /></Field>
              <Field label="Storage location" required><input className={inputCls} value={f.storage_location || ""} onChange={(e) => setF({ ...f, storage_location: e.target.value })} data-testid="ev-add-storage" /></Field>
            </div>
            <Field label="Location found"><input className={inputCls} value={f.location_found || ""} onChange={(e) => setF({ ...f, location_found: e.target.value })} data-testid="ev-add-location" /></Field>
          </div>
          <DialogFooter>
            <button className={btnGhost} onClick={onClose}>Cancel</button>
            <button className={btnPrimary} disabled={!f.description || !f.category || !f.collected_by || !f.storage_location} data-testid="ev-add-submit"
              onClick={async () => { try { await ujmsLogEvidence(caseId, f); done("EVIDENCE_LOGGED — chain genesis entry created"); } catch (e) { fail(e); } }}>Log Item</button>
          </DialogFooter>
        </>}
        {type === "transfer" && <>
          <DialogHeader><DialogTitle>Transfer — {item.item_number}</DialogTitle></DialogHeader>
          <Field label="Action" required>
            <select className={selectCls} value={f.action || ""} onChange={(e) => setF({ ...f, action: e.target.value })} data-testid="ev-transfer-action">
              <option value="">Select…</option>
              {["TRANSFER", "CHECK_OUT", "CHECK_IN"].map((a) => <option key={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="To custodian" required><input className={inputCls} value={f.to_custodian || ""} onChange={(e) => setF({ ...f, to_custodian: e.target.value })} placeholder="ISP Forensic Lab…" data-testid="ev-transfer-to" /></Field>
          <Field label="Purpose" required><input className={inputCls} value={f.purpose || ""} onChange={(e) => setF({ ...f, purpose: e.target.value })} placeholder="DNA analysis…" data-testid="ev-transfer-purpose" /></Field>
          <DialogFooter>
            <button className={btnGhost} onClick={onClose}>Cancel</button>
            <button className={btnPrimary} disabled={!f.action || !f.to_custodian || !f.purpose} data-testid="ev-transfer-submit"
              onClick={async () => { try { await ujmsTransferEvidence(item.id, f); done("Chain entry appended (hash-linked)"); } catch (e) { fail(e); } }}>Record Transfer</button>
          </DialogFooter>
        </>}
        {type === "dispose" && <>
          <DialogHeader><DialogTitle>Dispose — {item.item_number}</DialogTitle></DialogHeader>
          <Field label="Method" required>
            <select className={selectCls} value={f.method || ""} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="ev-dispose-method">
              <option value="">Select…</option>
              {["DESTROYED", "RELEASED", "RETURNED"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Reason" required><input className={inputCls} value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Court order of destruction…" data-testid="ev-dispose-reason" /></Field>
          <DialogFooter>
            <button className={btnGhost} onClick={onClose}>Cancel</button>
            <button className={btnDanger} disabled={!f.method || !f.reason} data-testid="ev-dispose-submit"
              onClick={async () => { try { await ujmsDisposeEvidence(item.id, f); done("EVIDENCE_DISPOSED — final chain entry"); } catch (e) { fail(e); } }}>Dispose</button>
          </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
}
